import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';

import { blogApi, type BlogLanguage, type BlogPostStatus } from '../../api/blog';
import BlogNav from './BlogNav';

const STATUSES: BlogPostStatus[] = ['Draft', 'Scheduled', 'Published', 'NeedsAttention', 'Archived'];

export const statusLabel: Record<BlogPostStatus, string> = {
  Draft: 'Draft',
  Scheduled: 'Scheduled',
  Published: 'Published',
  NeedsAttention: 'Needs attention',
  Archived: 'Archived',
};

export function formatWhen(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
    : '—';
}

export default function BlogPostsPage() {
  // The automation page and the alerts link here with ?status=NeedsAttention: "the articles that
  // were parked" has to be one click away from the message saying one was.
  const [params] = useSearchParams();
  const asked = params.get('status') as BlogPostStatus | null;

  const [status, setStatus] = useState<BlogPostStatus | ''>(asked && STATUSES.includes(asked) ? asked : '');
  const [language, setLanguage] = useState<BlogLanguage | ''>('');
  const [search, setSearch] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [page, setPage] = useState(1);

  const { data, isPending, error } = useQuery({
    queryKey: ['blog-posts', status, language, submitted, page],
    queryFn: () => blogApi.posts({ status, language, search: submitted, page }),
  });

  // Asked for separately and regardless of the filter: a parked article is the one thing on this
  // page somebody has to act on, and it should not be hidden by whatever filter happens to be set.
  const { data: parked } = useQuery({
    queryKey: ['blog-posts', 'NeedsAttention', '', '', 1],
    queryFn: () => blogApi.posts({ status: 'NeedsAttention', page: 1 }),
  });

  function onSearch(e: FormEvent) {
    e.preventDefault();
    setSubmitted(search.trim());
    setPage(1);
  }

  return (
    <>
      <BlogNav />

      {parked && parked.totalCount > 0 && status !== 'NeedsAttention' && (
        <div className="alert alert--warning">
          <div className="alert__head">
            <h3>
              {parked.totalCount} generated {parked.totalCount === 1 ? 'article is' : 'articles are'} parked
            </h3>
            <button
              type="button"
              className="btn btn--small"
              onClick={() => {
                setStatus('NeedsAttention');
                setPage(1);
              }}
            >
              Show
            </button>
          </div>
          <p>
            They failed a check or the reviewer, so they were not published. Each one carries the reviewer's notes:
            fix and publish it, or delete it.
          </p>
        </div>
      )}

      <section className="card">
        <div className="topbar topbar--flush">
          <h2>Posts</h2>
          <Link className="btn btn--primary" to="/blog/new">
            New post
          </Link>
        </div>

        <form className="toolbar" onSubmit={onSearch}>
          <select
            className="field__input field__input--short"
            aria-label="Status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as BlogPostStatus | '');
              setPage(1);
            }}
          >
            <option value="">Any status</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {statusLabel[s]}
              </option>
            ))}
          </select>

          <select
            className="field__input field__input--short"
            aria-label="Language"
            value={language}
            onChange={(e) => {
              setLanguage(e.target.value as BlogLanguage | '');
              setPage(1);
            }}
          >
            <option value="">Any language</option>
            <option value="Bg">Bulgarian</option>
            <option value="En">English</option>
          </select>

          <input
            className="field__input"
            type="search"
            placeholder="Title or slug"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="submit" className="btn">
            Search
          </button>
        </form>

        {error && <p className="status-error">The posts could not be loaded.</p>}
        {isPending && <p className="muted">Loading…</p>}

        {data && data.items.length === 0 && (
          <p className="muted">
            {submitted || status || language ? 'Nothing matches that filter.' : 'No posts yet. Write the first one.'}
          </p>
        )}

        {data && data.items.length > 0 && (
          <>
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Lang</th>
                    <th>Status</th>
                    <th>Goes live / went live</th>
                    <th>Words</th>
                    <th>Written by</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((post) => (
                    <tr key={post.id}>
                      <td>
                        <Link to={`/blog/${post.id}`} className="strong">
                          {post.title}
                        </Link>
                        <div className="muted">/{post.slug}</div>
                      </td>
                      <td>{post.language === 'En' ? 'EN' : 'BG'}</td>
                      <td>
                        <span className={`badge badge--${post.status.toLowerCase()}`}>{statusLabel[post.status]}</span>
                      </td>
                      <td>{formatWhen(post.status === 'Scheduled' ? post.publishAt : post.publishedAt)}</td>
                      <td>{post.wordCount}</td>
                      <td>
                        {post.origin === 'Ai' ? (
                          <>AI{post.reviewScore !== null && <span className="muted"> · score {post.reviewScore}</span>}</>
                        ) : (
                          'Person'
                        )}
                      </td>
                      <td>
                        <Link className="btn btn--small" to={`/blog/${post.id}`}>
                          Edit
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="toolbar">
              <button type="button" className="btn btn--small" disabled={!data.hasPrevious} onClick={() => setPage(page - 1)}>
                ←
              </button>
              <span className="muted">
                {data.page} / {Math.max(1, data.totalPages)} · {data.totalCount} posts
              </span>
              <button type="button" className="btn btn--small" disabled={!data.hasNext} onClick={() => setPage(page + 1)}>
                →
              </button>
            </div>
          </>
        )}
      </section>
    </>
  );
}
